interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

const BASE_URL = '/api';

async function request<T>(method: string, path: string, body?: unknown): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  // Inject x-agent-id header for warehouse and print-jobs flows so the
  // backend can route print jobs to this terminal's print agent.
  if (path.includes('/warehouse/') || path.includes('/labels/print-jobs')) {
    const agentId = typeof localStorage !== 'undefined' ? localStorage.getItem('cbt_terminal_id') : null;
    if (agentId) headers['x-agent-id'] = agentId;
  }

  // 2026-09-13: nunca leer ni guardar respuestas de la API en la caché del navegador.
  // Una redirección 301 cacheada (migración DNS) o una respuesta vieja servida por
  // Safari dejaban pantallas en "Error al cargar…" sin llegar al servidor.
  const opts: RequestInit = {
    method,
    headers,
    credentials: 'include',
    cache: 'no-store',
  };

  if (body && method !== 'GET') {
    opts.body = JSON.stringify(body);
  }

  const res = await fetch(`${BASE_URL}${path}`, opts);

  // Auto-refresh on 401
  if (res.status === 401 && !path.includes('/auth/')) {
    const refreshRes = await fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });

    if (refreshRes.ok) {
      // Retry original request
      const retryRes = await fetch(`${BASE_URL}${path}`, opts);
      return retryRes.json();
    }

    // Redirect to login
    window.location.href = '/login';
    return { success: false, error: 'Session expired' };
  }

  return res.json();
}

async function requestFormData<T>(path: string, fd: FormData): Promise<ApiResponse<T>> {
  // Do NOT set Content-Type — fetch fills it with the multipart boundary.
  const opts: RequestInit = { method: 'POST', body: fd, credentials: 'include', cache: 'no-store' };
  const res = await fetch(`${BASE_URL}${path}`, opts);

  if (res.status === 401 && !path.includes('/auth/')) {
    const refreshRes = await fetch(`${BASE_URL}/auth/refresh`, { method: 'POST', credentials: 'include' });
    if (refreshRes.ok) {
      const retryRes = await fetch(`${BASE_URL}${path}`, opts);
      return retryRes.json();
    }
    window.location.href = '/login';
    return { success: false, error: 'Session expired' };
  }

  return res.json();
}

async function requestBlob(path: string): Promise<Blob> {
  const opts: RequestInit = { method: 'GET', credentials: 'include', cache: 'no-store' };
  let res = await fetch(`${BASE_URL}${path}`, opts);

  if (res.status === 401 && !path.includes('/auth/')) {
    const refreshRes = await fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (refreshRes.ok) {
      res = await fetch(`${BASE_URL}${path}`, opts);
    } else {
      window.location.href = '/login';
      throw new Error('Session expired');
    }
  }

  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  return res.blob();
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  getBlob: (path: string) => requestBlob(path),
  post: <T>(path: string, body: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
  postFormData: <T>(path: string, fd: FormData) => requestFormData<T>(path, fd),
};
